import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCcw, Home, CheckCircle2, Zap, Trophy, ChevronRight } from 'lucide-react';
import Mascot from '../components/Mascot';
import { playSound } from '../utils/sounds';
import Lottie from "lottie-react";

export default function ResultsView({ score, total, lesson, startSet, setView, userProgress, game }) {
  const activeSkin = game?.activeSkin || userProgress?.currentSkin || 'bribark';
  const perfectSession = !!game?.perfectSession;
  const sessionXP = Number(game?.sessionXP || 0);

  // --- ESTADOS ---
  const [showAchievement, setShowAchievement] = useState(false);
  const [animationData, setAnimationData] = useState(null);
  const [currentModuleNum, setCurrentModuleNum] = useState(null); // ✅ NUEVO ESTADO

  const isUnitReview = lesson?.id?.includes('review');
  const safeTotal = total || 1;
  const percentage = Math.round((score / safeTotal) * 100);
  const isPassed = percentage >= 70;

  const perfectBonus = isPassed && perfectSession ? 50 : 0;
  const earnedXP = sessionXP + perfectBonus;

  // --- FRASES (tu código actual) ---
  const phrases = useMemo(() => {
    const isDolly = String(activeSkin).startsWith('dolly');

    const dollyPhrases = [
      "¡Lo lograste! Cerraste este módulo con mucho progreso 💖",
      "¡Excelente trabajo! Todo este esfuerzo ya se nota.",
      "¡Qué bonito avance! Este módulo ya es parte de ti.",
      "¡Perfecto! Sabía que llegarías hasta aquí.",
      "Respira, sonríe… estás listo para el siguiente módulo ✨",
      "¡Qué bonito progreso! Sigue así.",
      "¡Listo! Vamos con el siguiente set ✨"
    ];

    const bribarkPhrases = [
      "¡Boom! Set superado 💥",
      "¡Eso! Nadie te para.",
      "¡Nivel dominado! Vamos por más.",
      "¡Hecho! ¿Listo para el siguiente?",
      "¡Boom! Módulo completado 💥",
      "¡Eso! Este módulo ya está dominado.",
      "¡Bien jugado! Otro módulo a la bolsa.",
      "¡Nivel superado! Vamos por el siguiente.",
      "¡Hecho! El próximo módulo nos espera 😎"
    ];

    const perfectDollyPhrases = [
      "¡PERFECTO! Eres una superestrella brillante ✨",
      "¡Increíble! Ni un solo error, ¡qué estilo! 💖",
      "¡Perfecto! Todo salió tal como debía ✨",
      "¡Impecable! Cerraste el módulo sin errores 💖",
      "¡Qué elegancia! Dominaste cada parte del módulo.",
      "¡Hermoso trabajo! Un módulo completo y perfecto 🌸",
      "¡Así se hace! Este módulo fue redondo.",
      "Perfect score! You did beautifully ✨",
      "Flawless work. You should be proud 💖"
    ];

    const perfectBribarkPhrases = [
      "¡GOD MODE ON! 100% clean, puro nivel 🔥",
      "¡PERFECT! Directo al Hall of Fame 🏆",
      "¡Clean run! Módulo sin fallas 💥",
      "¡Full control! 100% completed 😎",
      "¡Master level! No dejaste nada al azar.",
      "¡Unstoppable! Este módulo fue all yours.",
      "¡Pro move! Rendimiento perfecto 🏆"
    ];

    const retryPhrases = [
      "¡No te rindas! Casi lo tienes.",
      "Repasemos un poco y volvamos a intentar.",
      "El error es parte de aprender. ¡Tú puedes!",
      "¡Ánimo! La próxima sale seguro."
    ];

    return {
      win: perfectSession
        ? (isDolly ? perfectDollyPhrases : perfectBribarkPhrases)
        : (isDolly ? dollyPhrases : bribarkPhrases),
      retry: retryPhrases
    };
  }, [activeSkin, perfectSession]);

  const [mascotData, setMascotData] = useState({
    type: activeSkin,
    message: '',
    emotion: 'happy'
  });

  // --- EFECTO: Cargar video si es final de módulo ---
  useEffect(() => {
    if (isPassed && isUnitReview) {
      const moduleMatch = lesson.id.match(/m(\d+)/);
      const moduleNum = moduleMatch ? moduleMatch[1] : "1";
      
      setCurrentModuleNum(moduleNum); // ✅ GUARDAR EN ESTADO

      fetch(`/animations/modulo${moduleNum}.json`)
        .then(res => {
          if (res.ok) return res.json();
          throw new Error("Video no encontrado");
        })
        .then(data => {
          setAnimationData(data);
          setShowAchievement(true);
          playSound('modulo1');
        })
        .catch(err => console.log(`No hay video para módulo ${moduleNum}, saltando...`));
    }
  }, [isPassed, isUnitReview, lesson.id]);

  // --- EFECTO: Configurar mascota y sonidos ---
  useEffect(() => {
    if (!showAchievement) {
      const pool = isPassed ? phrases.win : phrases.retry;
      const msg = pool[Math.floor(Math.random() * pool.length)];
      
      setMascotData({
        type: activeSkin,
        message: msg,
        emotion: isPassed ? 'happy' : 'sad'
      });

      playSound(isPassed ? 'win' : 'gameover');
    }
  }, [isPassed, activeSkin, phrases, showAchievement]);

  const handleGoBack = () => {
    if (lesson?.returnView) setView(lesson.returnView);
    else if (lesson?.id && String(lesson.id).startsWith('practice')) setView('vocabulary');
    else if (lesson?.id && String(lesson.id).startsWith('weekly-challenge')) setView('weekly-challenge');
    else if (lesson?.id && String(lesson.id).startsWith('weekly')) setView('lobby');
    else setView('sets');
  };

  const titleColor = isPassed ? 'text-green-600' : 'text-red-600';
  const topBarColor = isPassed ? 'bg-green-400' : 'bg-red-400';
  const pctColor = isPassed ? 'text-green-500' : 'text-red-500';
  const progressColor = isPassed ? 'bg-green-500' : 'bg-red-500';

  const handleCloseAchievement = () => {
    setShowAchievement(false);
  };

  const handleAnimationComplete = () => {
    setTimeout(() => {
      handleCloseAchievement();
    }, 1000);
  };

  // --- VISTA DEL VIDEO DE LOGRO ---
  if (showAchievement && animationData) {
    return (
      <div className="fixed inset-0 z-[100] bg-indigo-950/95 backdrop-blur-md flex flex-col items-center justify-between p-10 text-white animate-in fade-in duration-500">
        
        {/* 1. Encabezado compacto */}
        <div className="text-center space-y-2 mt-4">
          <Trophy size={40} className="mx-auto text-yellow-400 animate-bounce" />
          <h1 className="text-2xl font-black tracking-tighter italic uppercase">
            ¡MÓDULO {currentModuleNum} COMPLETADO! {/* ✅ AHORA SÍ FUNCIONA */}
          </h1>
          <p className="text-indigo-300 font-bold uppercase tracking-widest text-[10px]">
            Maestría absoluta demostrada
          </p>
        </div>

        {/* 2. VIDEO MÁS PEQUEÑO */}
        <div className="w-64 h-64 relative flex items-center justify-center">
          <div className="absolute inset-0 bg-indigo-500/20 blur-3xl rounded-full animate-pulse" />
          <Lottie 
            animationData={animationData} 
            loop={false}
            onComplete={handleAnimationComplete}
            className="relative z-10"
          />
        </div>

        {/* 3. Panel de Recompensa y Botón */}
        <div className="w-full max-w-xs space-y-6 mb-4">
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-3xl border border-white/20 text-center shadow-xl">
            <p className="text-3xl font-black text-amber-400">+{earnedXP} XP</p>
            <p className="text-[10px] font-black text-indigo-200 uppercase tracking-widest">Recompensa total</p>
          </div>

          <button
            onClick={handleCloseAchievement}
            className="w-full bg-white text-indigo-900 py-4 rounded-[2rem] font-black text-lg shadow-[0_8px_0_0_#cbd5e1] hover:translate-y-1 hover:shadow-[0_4px_0_0_#cbd5e1] active:translate-y-2 active:shadow-none transition-all flex items-center justify-center gap-2 relative z-[110] group"
          >
            CONTINUAR <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    );
  }

  // --- VISTA DE RESULTADOS NORMAL ---
  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] text-center space-y-6 animate-in zoom-in duration-500 pb-20">

      <div className="space-y-2">
        {isPassed && perfectSession && (
          <div className="bg-amber-400 text-white px-6 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] animate-bounce mx-auto w-fit shadow-lg border-2 border-white">
            ⭐ Lección Perfecta ⭐
          </div>
        )}

        <h2 className={`text-4xl font-black italic tracking-tighter ${titleColor}`}>
          {isPassed ? '¡SET COMPLETADO!' : '¡INTÉNTALO DE NUEVO!'}
        </h2>

        <div
          className={`flex items-center justify-center gap-2 px-4 py-2 rounded-2xl border-2 animate-in slide-in-from-top-10 duration-700 mx-auto w-fit
            ${earnedXP > 0 ? 'bg-amber-100 text-amber-600 border-amber-200' : 'bg-slate-100 text-slate-400 border-slate-200'}`}
        >
          <Zap size={20} fill="currentColor" className={earnedXP > 0 ? 'animate-pulse' : ''} />
          <span className="text-xl font-black">+{earnedXP} XP</span>

          {perfectBonus > 0 && (
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-200/60 px-2 py-1 rounded-full">
              +{perfectBonus} bonus
            </span>
          )}
        </div>
      </div>

      <div className="scale-95 drop-shadow-2xl">
        <Mascot
          type={mascotData.type}
          emotion={mascotData.emotion}
          message={mascotData.message}
          isJumping={isPassed}
        />
      </div>

      <div className="bg-white p-6 rounded-[2.5rem] shadow-xl border-4 border-slate-50 w-full max-w-xs relative overflow-hidden">
        <div className={`absolute top-0 left-0 w-full h-3 ${topBarColor}`}></div>

        <div className="flex justify-around items-center mb-6 pt-2">
          <div className="text-center">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Aciertos</p>
            <p className="text-3xl font-black text-slate-700">
              {score}<span className="text-slate-300 text-xl">/{total}</span>
            </p>
          </div>

          <div className="text-center">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Precisión</p>
            <p className={`text-3xl font-black ${pctColor}`}>{percentage}%</p>
          </div>
        </div>

        <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
          <div
            className={`h-full rounded-full transition-all duration-1000 ease-out ${progressColor}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      <div className="w-full space-y-3 pt-2 px-6">
        {isPassed ? (
          <button
            onClick={handleGoBack}
            className="w-full bg-indigo-600 text-white py-4 rounded-3xl font-black text-xl shadow-xl shadow-indigo-200 border-b-4 border-indigo-900 active:border-b-0 active:translate-y-1 transition-all flex items-center justify-center gap-3"
          >
            CONTINUAR <CheckCircle2 size={24} strokeWidth={3} />
          </button>
        ) : (
          <button
            onClick={() => startSet(lesson)}
            className="w-full bg-red-600 text-white py-4 rounded-3xl font-black text-xl shadow-xl shadow-red-200 border-b-4 border-red-900 active:border-b-0 active:translate-y-1 transition-all flex items-center justify-center gap-3"
          >
            REINTENTAR <RefreshCcw size={24} strokeWidth={3} />
          </button>
        )}

        <button
          onClick={() => setView(lesson?.returnView || 'lobby')}
          className="w-full text-slate-400 font-black py-3 hover:text-slate-600 transition-colors flex items-center justify-center gap-2 text-xs uppercase tracking-widest"
        >
          <Home size={14} /> {lesson?.returnView ? 'Volver al Desafío' : 'Salir al Menú Principal'}
        </button>
      </div>
    </div>
  );
}
